import React from "react";
import { useNavigate } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { AUTHENTICATE_OPERATOR_ACCOUNT } from "../../constants/graphql/auth-queries";
import {
  Button,
  LinearProgressBar,
  Modal,
  Snackbar,
  Textfield,
} from "../../components/core";
import { useDispatch } from "react-redux";
import { setUserAccessPermission } from "../../features/access-permissions";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [snack, setSnack] = React.useState({
    isOpen: false,
    message: "",
    type: "success",
  });
  const [loading, setLoading] = React.useState(false);
  const [getOperator, { data, error }] = useLazyQuery(
    AUTHENTICATE_OPERATOR_ACCOUNT,
  );
  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  const handleLogin = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);

      getOperator({
        variables: { userEmail: email, password: password },
        fetchPolicy: "no-cache",
      }).then((response) => {
        if (!response.data) {
          if (
            response.error.message.includes(
              "Incorrect operator account email or password.",
            )
          ) {
            setValidation({
              icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
              title: "Login Error",
              message: "Invalid login credentials.",
              open: true,
              actions: (
                <div className="w-full">
                  <Button
                    onClick={() => setValidation({ open: false })}
                    value={"OK"}
                    className={"p-2 w-full"}
                  />
                </div>
              ),
            });
          } else if (
            response.error.message.includes(
              "Cannot return null for non-nullable field OperatorType.operatorRole.",
            )
          ) {
            setValidation({
              icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
              title: "Login Error",
              message:
                "Your account requires an update due to recent system changes. Please contact MBOX Smart Lockers to update your access.",
              open: true,
              actions: (
                <div className="w-full">
                  <Button
                    onClick={() => setValidation({ open: false })}
                    value={"OK"}
                    className={"p-2 w-full"}
                  />
                </div>
              ),
            });
          }
          setLoading(false);
          return;
        }

        const operatorAccount = response.data.authenticateOperatorAccount;

        if (!operatorAccount) {
          setSnack({
            isOpen: true,
            message: "Invalid credentials. Email or Password is incorrect.",
            type: "error",
          });
          setLoading(false);
          return;
        }

        // Check if account is active
        if (!operatorAccount.active) {
          setLoading(false);
          setValidation({
            icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
            title: "Login Error",
            message: "Your account is disabled. Please contact your admin.",
            open: true,
            actions: (
              <div className="w-full">
                <Button
                  onClick={() => setValidation({ open: false })}
                  value={"OK"}
                  className={"p-2 w-full"}
                />
              </div>
            ),
          });
          return;
        }

        // Process login for active account - use accessPermissions only
        try {
          localStorage.setItem("token", operatorAccount.operator.operatorId);
          localStorage.setItem("version", process.env.REACT_APP_VERSION);

          console.log({ operatorAccount });

          // Store user data
          localStorage.setItem(
            "user",
            JSON.stringify({
              operatorAccountId: operatorAccount.operatorAccountId,
              email: operatorAccount.userEmail,
              username: operatorAccount.username,
              operator_id: operatorAccount.operator.operatorId,
              company: operatorAccount.operator.company,
              admin_access: operatorAccount.adminAccess,
            }),
          );

          // Store permissions - conditional based on admin access
          let permissionsStr;
          if (operatorAccount.adminAccess !== true) {
            // Non-admin: use operatorAccount.accessPermissions
            permissionsStr = operatorAccount.accessPermissions;
          } else {
            // Admin: use operator.operatorRole.accessPermissions
            permissionsStr =
              operatorAccount.operator.operatorRole.accessPermissions;
          }

          const permissions = JSON.parse(permissionsStr);
          localStorage.setItem("permission", permissionsStr);
          dispatch(setUserAccessPermission(permissions));

          setLoading(false);
          // Redirect back to where the user was before the session expired
          let returnUrl = sessionStorage.getItem("returnUrl");
          if (returnUrl) {
            sessionStorage.removeItem("returnUrl");
            // Strip the basename from returnUrl to avoid duplication
            // (Router already adds it via basename prop)
            const basename = process.env.REACT_APP_BASE_ENVIRONMENT;
            if (returnUrl.startsWith(basename)) {
              returnUrl = returnUrl.slice(basename.length);
            }
          }
          navigate(returnUrl || "agents");
        } catch (error) {
          console.error("Error processing login:", error);
          setSnack({
            isOpen: true,
            message: "An error occurred during login. Please try again.",
            type: "error",
          });
          setLoading(false);
        }
      });
    } catch (error) {
      console.error("Login failed:", error);
      setLoading(false);
    }
  };

  // Redirect if already logged in
  React.useEffect(() => {
    if (localStorage.getItem("user")) {
      navigate("agents");
    }
  }, [navigate]);

  // Cross-tab login sync — if another tab logs in, reload this page
  React.useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "user" && event.newValue) {
        window.location.reload();
      }
      if (event.key === "permission" && event.newValue) {
        if (localStorage.getItem("user")) {
          window.location.reload();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <>
      <div className="flex items-center justify-center h-screen bg-primary-primaryYellow">
        <div>
          <LinearProgressBar label="" open={loading} className="rounded-md" />
          <div className="content bg-white w-fit p-20 rounded-lg shadow-lg m-auto">
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
              <img
                className="mx-auto w-auto"
                src={`${process.env.PUBLIC_URL}/assets/images/mbox_logo.jpg`}
                alt="Your Company"
              />
              <h2 className="mt-10 text-center text-3xl font-bold leading-9 tracking-tight text-gray-900 tracking-wide">
                Sign in to your account
              </h2>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
              <form className="space-y-6" onSubmit={handleLogin} method="POST">
                <Textfield
                  type={"email"}
                  name={"email"}
                  placeholder={"Email address"}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required={true}
                />
                <Textfield
                  type={"password"}
                  name={"password"}
                  placeholder={"Password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required={true}
                />
                <Button value={"Sign in"} className={"p-2"} />
              </form>
            </div>
          </div>
        </div>
        <Snackbar
          isOpen={snack.isOpen}
          message={snack.message}
          onClose={() => setSnack((prev) => ({ ...prev, isOpen: false }))}
          type={snack.type}
        />
        <Modal
          icon={validation.icon}
          title={validation.title}
          message={validation.message}
          open={validation.open}
          actions={validation.actions}
        />
      </div>
    </>
  );
};

export default LoginPage;
